<!--
  CommunityGuard background-check dialog.
  Layout: left rail (sections) · center (current section) · right rail (case summary + decision).
-->
<div class="bgc-shell">

  <!-- ─── Top bar ───────────────────────────────────────────────────────────── -->
  <header class="bgc-topbar">
    <div class="bgc-topbar-left">
      <button mat-icon-button class="bgc-back-btn" (click)="closeDialog()" matTooltip="Back"><mat-icon>arrow_back</mat-icon></button>
      <span class="bgc-back-text" (click)="closeDialog()">Back</span>
      <div class="bgc-brand">
        <div class="bgc-brand-icon"><mat-icon>shield</mat-icon></div>
        <div class="bgc-brand-text">
          <div class="bgc-brand-title">Background Check</div>
          <div class="bgc-brand-sub">CommunityGuard Registry · Sex Offender Search</div>
        </div>
      </div>
    </div>

    <div class="bgc-topbar-center">
      <div class="bgc-case-chip" *ngIf="data?.selectedRecord?.name">
        <mat-icon>person</mat-icon>
        <span>{{ data?.selectedRecord?.name }}</span>
        <span class="bgc-case-chip-sub" *ngIf="data?.selectedRecord?.referralID">REF-{{ data?.selectedRecord?.referralID }}</span>
        <span class="bgc-case-chip-sub" *ngIf="!data?.selectedRecord?.referralID && data?.selectedRecord?.coreID">CORE-{{ data?.selectedRecord?.coreID }}</span>
      </div>
    </div>

    <div class="bgc-topbar-right">
      <span [class]="decisionBadgeClass(currentRun.decisionStatus)">
        <mat-icon>fact_check</mat-icon> {{ decisionLabel(currentRun.decisionStatus) }}
      </span>
      <button mat-icon-button (click)="closeDialog()" matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <!-- ─── Body ──────────────────────────────────────────────────────────────── -->
  <div class="bgc-body">

    <!-- ─── Left rail: section nav ─────────────────────────────────────── -->
    <aside class="bgc-rail">
      <div class="bgc-rail-heading">Workflow</div>
      <nav class="bgc-rail-nav">
        <button class="bgc-rail-item"
                *ngFor="let s of sections"
                [class.active]="activeSection === s"
                (click)="selectSection(s)">
          <span class="bgc-rail-icon-wrap">
            <mat-icon class="bgc-rail-icon">
              <ng-container [ngSwitch]="s">
                <ng-container *ngSwitchCase="'Intake'">tune</ng-container>
                <ng-container *ngSwitchCase="'Search Results'">manage_search</ng-container>
                <ng-container *ngSwitchCase="'Decision'">gavel</ng-container>
                <ng-container *ngSwitchCase="'History'">history</ng-container>
                <ng-container *ngSwitchCase="'Notes'">sticky_note_2</ng-container>
              </ng-container>
            </mat-icon>
          </span>
          <span class="bgc-rail-label">{{ s }}</span>
          <span class="bgc-rail-count" *ngIf="s === 'Search Results' && currentRun.totalMatches > 0">
            {{ currentRun.matches.length }}
          </span>
        </button>
      </nav>

      <div class="bgc-rail-foot">
        <mat-icon>verified_user</mat-icon>
        <span>CommunityGuard Registry</span>
      </div>
    </aside>

    <!-- ─── Center: section content ───────────────────────────────────── -->
    <main class="bgc-main">

      <!-- INTAKE ──────────────────────────────────────────────────────── -->
      <ng-container *ngIf="activeSection === 'Intake'">
        <div class="bgc-section-head">
          <div>
            <h2>Search Parameters</h2>
            <p>Refine your query before running the check. Fields are pre-populated from the case record.</p>
          </div>
          <button mat-flat-button color="primary" class="bgc-btn-run"
                  [disabled]="isRunning"
                  (click)="runCheck()">
            <mat-icon *ngIf="!isRunning">play_arrow</mat-icon>
            <mat-progress-spinner *ngIf="isRunning" diameter="20" mode="indeterminate" color="accent"></mat-progress-spinner>
            <span>{{ isRunning ? 'Running…' : 'Run Background Check' }}</span>
          </button>
        </div>

        <form [formGroup]="searchForm" class="bgc-form-grid">

          <div class="bgc-form-section">
            <div class="bgc-form-section-title"><mat-icon>person_search</mat-icon> Subject</div>
            <div class="bgc-form-section-fields">
              <div class="bgc-form-row">
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>First Name *</mat-label>
                  <input matInput formControlName="first_name" required />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Last Name</mat-label>
                  <input matInput formControlName="last_name" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Date of Birth</mat-label>
                  <input matInput [matDatepicker]="dobPicker" formControlName="date_of_birth" placeholder="MM/DD/YYYY" />
                  <mat-datepicker-toggle matSuffix [for]="dobPicker"></mat-datepicker-toggle>
                  <mat-datepicker #dobPicker></mat-datepicker>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field bgc-field-wide">
                  <mat-label>General Search (matches name, address, registration ID, aliases)</mat-label>
                  <input matInput formControlName="q" placeholder="optional broader query" />
                </mat-form-field>
              </div>
            </div>
          </div>

          <div class="bgc-form-section">
            <div class="bgc-form-section-title"><mat-icon>location_on</mat-icon> Location</div>
            <div class="bgc-form-section-fields">
              <div class="bgc-form-row">
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>ZIP Code</mat-label>
                  <input matInput formControlName="zip_code" maxlength="10" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>City</mat-label>
                  <input matInput formControlName="city" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>State</mat-label>
                  <mat-select formControlName="state">
                    <mat-option value="">Any</mat-option>
                    <mat-option *ngFor="let s of stateOptions" [value]="s">{{ s }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Latitude</mat-label>
                  <input matInput type="number" formControlName="latitude" step="0.000001" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Longitude</mat-label>
                  <input matInput type="number" formControlName="longitude" step="0.000001" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Radius (miles)</mat-label>
                  <input matInput type="number" formControlName="radius_miles" step="0.1" />
                </mat-form-field>
              </div>
            </div>
          </div>

          <div class="bgc-form-section">
            <div class="bgc-form-section-title"><mat-icon>filter_alt</mat-icon> Filters</div>
            <div class="bgc-form-section-fields">
              <div class="bgc-form-row">
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Risk Level</mat-label>
                  <mat-select formControlName="risk_level">
                    <mat-option value="">Any</mat-option>
                    <mat-option *ngFor="let r of riskLevelOptions" [value]="r">{{ r }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Status</mat-label>
                  <mat-select formControlName="status">
                    <mat-option value="">Any</mat-option>
                    <mat-option *ngFor="let s of statusOptions" [value]="s">{{ s }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Registration Type</mat-label>
                  <mat-select formControlName="registration_type">
                    <mat-option value="">Any</mat-option>
                    <mat-option *ngFor="let t of registrationOptions" [value]="t">{{ t }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>DOB Year</mat-label>
                  <input matInput type="number" formControlName="dob_year" placeholder="e.g. 1985" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Min Age</mat-label>
                  <input matInput type="number" formControlName="age_min" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Max Age</mat-label>
                  <input matInput type="number" formControlName="age_max" />
                </mat-form-field>
              </div>
            </div>
          </div>

          <div class="bgc-form-section">
            <div class="bgc-form-section-title"><mat-icon>sort</mat-icon> Result Ordering</div>
            <div class="bgc-form-section-fields">
              <div class="bgc-form-row">
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Sort By</mat-label>
                  <mat-select formControlName="sort_by">
                    <mat-option *ngFor="let s of sortByOptions" [value]="s">{{ s }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Sort Order</mat-label>
                  <mat-select formControlName="sort_order">
                    <mat-option *ngFor="let s of sortOrderOptions" [value]="s">{{ s }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="bgc-field">
                  <mat-label>Per Page</mat-label>
                  <input matInput type="number" formControlName="per_page" min="1" max="100" />
                </mat-form-field>
              </div>
            </div>
          </div>
        </form>
      </ng-container>

      <!-- SEARCH RESULTS ──────────────────────────────────────────────── -->
      <ng-container *ngIf="activeSection === 'Search Results'">
        <div class="bgc-section-head">
          <div>
            <h2>Search Results</h2>
            <p *ngIf="currentRun.totalMatches > 0">
              {{ currentRun.totalMatches }} potential match<span *ngIf="currentRun.totalMatches !== 1">es</span>
              · best score
              <strong>{{ currentRun.matches[0]?.matchScore || 0 }}</strong>/100
            </p>
            <p *ngIf="currentRun.totalMatches === 0 && currentRun.backgroundCheckID">No matches found for the provided filters.</p>
            <p *ngIf="!currentRun.backgroundCheckID">Run a check from Intake to see results here.</p>
          </div>
          <div class="bgc-search-controls" *ngIf="currentRun.matches.length > 0">
            <mat-form-field appearance="outline" class="bgc-inline-search">
              <mat-icon matPrefix>search</mat-icon>
              <input matInput placeholder="Filter results…" [(ngModel)]="matchSearch" />
            </mat-form-field>
            <button mat-stroked-button (click)="activeSection='Intake'">
              <mat-icon>tune</mat-icon> Refine
            </button>
          </div>
        </div>

        <!-- Last reviewed context strip (only when a prior run hydrated this view) -->
        <div class="bgc-last-reviewed" *ngIf="currentRun.backgroundCheckID && currentRun.decisionStatus !== DECISION.PENDING_REVIEW">
          <mat-icon>history_edu</mat-icon>
          <span><strong>Last reviewed:</strong>
            <span [class]="decisionBadgeClass(currentRun.decisionStatus)">{{ decisionLabel(currentRun.decisionStatus) }}</span>
            <span class="bgc-last-reviewed-time">on {{ formatDateTime(currentRun.decisionUpdatedOn || currentRun.checkedOn) }}</span>
            <span class="bgc-last-reviewed-by" *ngIf="currentRun.checkedByName">· by {{ currentRun.checkedByName }}</span>
          </span>
        </div>
        <div class="bgc-last-reviewed bgc-last-reviewed-pending"
             *ngIf="currentRun.backgroundCheckID && currentRun.decisionStatus === DECISION.PENDING_REVIEW && currentRun.totalMatches > 0">
          <mat-icon>info</mat-icon>
          <span>This check ran {{ formatDateTime(currentRun.checkedOn) }} but a decision hasn't been recorded yet. Use the Decision tab to mark it.</span>
        </div>

        <!-- Risk banner -->
        <div *ngIf="currentRun.riskLabel" [class]="riskBannerClass(currentRun.riskLabel)">
          <div class="bgc-risk-banner-left">
            <mat-icon>verified</mat-icon>
            <div>
              <div class="bgc-risk-banner-label">{{ currentRun.riskLabel }}</div>
              <div class="bgc-risk-banner-reason">{{ currentRun.riskReason }}</div>
            </div>
          </div>
        </div>

        <!-- Provider error -->
        <div class="bgc-provider-error" *ngIf="currentRun.providerError">
          <mat-icon>error_outline</mat-icon>
          <span>{{ currentRun.providerError }}</span>
        </div>

        <!-- Match grid — clicking a card surfaces the full record in the
             right rail's selected-match panel, so we don't reserve any space
             for details in the main canvas. -->
        <div class="bgc-match-grid" *ngIf="filteredMatches.length > 0">
          <article class="bgc-match-card"
                   *ngFor="let m of filteredMatches"
                   [id]="'bgcMatchCard-' + m.matchID"
                   [class.selected]="selectedMatch?.matchID === m.matchID"
                   (click)="selectMatch(m)">
            <div class="bgc-match-photo">
              <img *ngIf="m.photoUrl" [src]="m.photoUrl" alt="{{ m.fullName }}" />
              <div *ngIf="!m.photoUrl" class="bgc-match-photo-fallback">
                {{ initials(m.fullName) }}
              </div>
              <div [class]="scoreClass(m.matchScore)">{{ m.matchScore }}</div>
            </div>
            <div class="bgc-match-body">
              <div class="bgc-match-name">{{ displayName(m) }}</div>
              <div class="bgc-match-meta">
                <span *ngIf="m.dateOfBirth">DOB {{ formatDate(m.dateOfBirth) }}</span>
                <span *ngIf="m.sex">· {{ m.sex }}</span>
                <span *ngIf="m.age">· Age {{ m.age }}</span>
                <span *ngIf="m.registrationId">· ID {{ m.registrationId }}</span>
              </div>
              <div class="bgc-match-meta bgc-match-addr" *ngIf="m.address || m.city || m.state || m.zipCode">
                <mat-icon>place</mat-icon>
                <span>{{ formatAddress(m) }}</span>
              </div>
              <div class="bgc-match-meta" *ngIf="m.distanceMiles !== undefined && m.distanceMiles !== null">
                <mat-icon>near_me</mat-icon> {{ m.distanceMiles | number:'1.1-1' }} mi away
              </div>
              <div class="bgc-match-tags">
                <span class="tag tag-risk" *ngIf="m.riskLevel" [ngClass]="riskLevelClass(m.riskLevel)">{{ m.riskLevel }}</span>
                <span class="tag tag-tier" *ngIf="m.tierLevel">{{ m.tierLevel }}</span>
                <span class="tag tag-source" *ngIf="m.sourceState">Source: {{ m.sourceState }}</span>
                <span class="tag tag-type" *ngIf="m.registrationType">{{ m.registrationType }}</span>
                <span class="tag tag-status" *ngIf="m.registrationStatus">{{ truncate(m.registrationStatus, 60) }}</span>
              </div>
              <div class="bgc-match-reasons" *ngIf="m.matchReasons">
                <mat-icon>insights</mat-icon> {{ m.matchReasons }}
              </div>
            </div>
          </article>
        </div>

      </ng-container>

      <!-- DECISION ────────────────────────────────────────────────────── -->
      <ng-container *ngIf="activeSection === 'Decision'">
        <div class="bgc-section-head">
          <div>
            <h2>Decision</h2>
            <p>Choose the outcome of this background check. This is recorded against the latest run and surfaces when the referral is accepted.</p>
          </div>
        </div>

        <div class="bgc-decision-grid">
          <button class="bgc-decision-card pending"
                  [class.active]="currentRun.decisionStatus === DECISION.PENDING_REVIEW"
                  [disabled]="isUpdatingDecision || !currentRun.backgroundCheckID"
                  (click)="updateDecision(DECISION.PENDING_REVIEW)">
            <mat-icon>hourglass_top</mat-icon>
            <span class="title">Pending Review</span>
            <span class="desc">Default state immediately after a run. Use when more review is needed.</span>
          </button>

          <button class="bgc-decision-card cleared"
                  [class.active]="currentRun.decisionStatus === DECISION.CLEARED"
                  [disabled]="isUpdatingDecision || !currentRun.backgroundCheckID"
                  (click)="updateDecision(DECISION.CLEARED)">
            <mat-icon>check_circle</mat-icon>
            <span class="title">Cleared</span>
            <span class="desc">No actionable matches. Safe to proceed with referral acceptance.</span>
          </button>

          <button class="bgc-decision-card blocked"
                  [class.active]="currentRun.decisionStatus === DECISION.MATCH_FOUND_BLOCKED"
                  [disabled]="isUpdatingDecision || !currentRun.backgroundCheckID"
                  (click)="updateDecision(DECISION.MATCH_FOUND_BLOCKED)">
            <mat-icon>block</mat-icon>
            <span class="title">Match Found — Blocked</span>
            <span class="desc">Confirmed match. Referral acceptance will warn the operator.</span>
          </button>

          <button class="bgc-decision-card override"
                  [class.active]="currentRun.decisionStatus === DECISION.MATCH_FOUND_OVERRIDE"
                  [disabled]="isUpdatingDecision || !currentRun.backgroundCheckID"
                  (click)="updateDecision(DECISION.MATCH_FOUND_OVERRIDE)">
            <mat-icon>verified_user</mat-icon>
            <span class="title">Match Found — Override Allowed</span>
            <span class="desc">Match found but reviewed and cleared for admission by ops.</span>
          </button>
        </div>
      </ng-container>

      <!-- HISTORY ─────────────────────────────────────────────────────── -->
      <ng-container *ngIf="activeSection === 'History'">
        <div class="bgc-section-head">
          <div>
            <h2>Verification History</h2>
            <p>Every check run on this case, newest first.</p>
          </div>
        </div>

        <div class="bgc-history-empty" *ngIf="!isLoadingHistory && historyRecords.length === 0">
          <mat-icon>history</mat-icon>
          <p>No prior runs for this case yet.</p>
        </div>

        <div class="bgc-history-loading" *ngIf="isLoadingHistory">
          <mat-progress-spinner diameter="32" mode="indeterminate" color="primary"></mat-progress-spinner>
          <span>Loading history…</span>
        </div>

        <div class="bgc-history-list" *ngIf="!isLoadingHistory && historyRecords.length > 0">
          <article class="bgc-history-card"
                   *ngFor="let h of historyRecords"
                   [class.selected]="selectedHistory?.backgroundCheckID === h.backgroundCheckID"
                   (click)="selectHistory(h)">
            <div class="bgc-history-card-top">
              <span [class]="riskLabelClass(h.riskLabel)">{{ h.riskLabel || '—' }}</span>
              <span [class]="decisionBadgeClass(h.decisionStatus)">{{ decisionLabel(h.decisionStatus) }}</span>
              <span class="bgc-history-when">{{ formatDateTime(h.checkedOn) }}</span>
              <span class="bgc-history-by" *ngIf="h.checkedByName">· {{ h.checkedByName }}</span>
              <span class="bgc-history-count">· {{ h.totalMatches }} match<span *ngIf="h.totalMatches !== 1">es</span></span>
              <button mat-flat-button color="primary" class="bgc-history-load"
                      (click)="loadHistoryIntoViewer(h); $event.stopPropagation()">
                <mat-icon>play_circle</mat-icon> Load
              </button>
            </div>
            <div class="bgc-history-reason" *ngIf="h.riskReason">{{ h.riskReason }}</div>

            <div class="bgc-history-matches" *ngIf="selectedHistory?.backgroundCheckID === h.backgroundCheckID && h.matches?.length">
              <div class="bgc-history-match"
                   *ngFor="let m of h.matches.slice(0, 5)">
                <div class="bgc-history-match-photo">
                  <img *ngIf="m.photoUrl" [src]="m.photoUrl" alt="" />
                  <div *ngIf="!m.photoUrl" class="bgc-match-photo-fallback small">{{ initials(m.fullName) }}</div>
                </div>
                <div class="bgc-history-match-body">
                  <div class="bgc-history-match-name">{{ m.fullName }}</div>
                  <div class="bgc-history-match-meta">{{ m.city }}, {{ m.state }} {{ m.zipCode }} · DOB {{ formatDate(m.dateOfBirth) }}</div>
                </div>
                <div [class]="scoreClass(m.matchScore)">{{ m.matchScore }}</div>
              </div>
              <div class="bgc-history-more" *ngIf="h.matches.length > 5">+ {{ h.matches.length - 5 }} more</div>
            </div>
          </article>
        </div>
      </ng-container>

      <!-- NOTES ────────────────────────────────────────────────────────── -->
      <ng-container *ngIf="activeSection === 'Notes'">
        <div class="bgc-section-head">
          <div>
            <h2>Notes &amp; Audit Trail</h2>
            <p>Case-scoped notes are visible across every screen the background check has been run from.</p>
          </div>
        </div>

        <div class="bgc-note-composer">
          <textarea
            [(ngModel)]="newNoteText"
            [maxlength]="NOTE_MAX_LENGTH"
            placeholder="Type a note (max {{ NOTE_MAX_LENGTH }} chars)…"
            rows="3"></textarea>
          <div class="bgc-note-composer-foot">
            <span class="bgc-note-count">{{ (newNoteText.length || 0) }} / {{ NOTE_MAX_LENGTH }}</span>
            <button mat-flat-button color="primary"
                    [disabled]="isSubmittingNote || !newNoteText.trim()"
                    (click)="submitNote()">
              <mat-icon *ngIf="!isSubmittingNote">send</mat-icon>
              <mat-progress-spinner *ngIf="isSubmittingNote" diameter="18" mode="indeterminate"></mat-progress-spinner>
              Add Note
            </button>
          </div>
        </div>

        <div class="bgc-note-empty" *ngIf="!isLoadingNotes && notes.length === 0">
          <mat-icon>sticky_note_2</mat-icon>
          <p>No notes yet. Be the first to record context for this case.</p>
        </div>

        <div class="bgc-note-list" *ngIf="!isLoadingNotes && notes.length > 0">
          <article class="bgc-note" *ngFor="let n of notes">
            <div class="bgc-note-avatar">{{ initials(n.createdByName) }}</div>
            <div class="bgc-note-body">
              <div class="bgc-note-head">
                <strong>{{ n.createdByName || 'Unknown' }}</strong>
                <span>{{ formatDateTime(n.createdOn) }}</span>
              </div>
              <p class="bgc-note-text">{{ n.noteText }}</p>
            </div>
          </article>
        </div>
      </ng-container>

    </main>

    <!-- ─── Right rail: compact case summary + selected match details ──── -->
    <aside class="bgc-summary">

      <!-- Top half: compact case summary -->
      <div class="bgc-summary-head">
        <!-- Two metric pills side-by-side to save vertical space. -->
        <div class="bgc-summary-grid">
          <div class="bgc-summary-cell">
            <div class="bgc-summary-label">Last Risk</div>
            <span [class]="riskLabelClass(currentRun.riskLabel)">{{ currentRun.riskLabel || 'Not Run' }}</span>
          </div>
          <div class="bgc-summary-cell">
            <div class="bgc-summary-label">Decision</div>
            <span [class]="decisionBadgeClass(currentRun.decisionStatus)">{{ decisionLabel(currentRun.decisionStatus) }}</span>
          </div>
        </div>

        <div class="bgc-summary-row">
          <div class="bgc-summary-label">Last Run</div>
          <div class="bgc-summary-value bgc-summary-value-sm">{{ formatDateTime(currentRun.checkedOn) || '—' }}</div>
        </div>

        <div class="bgc-summary-grid">
          <div class="bgc-summary-cell">
            <div class="bgc-summary-label">Candidates</div>
            <div class="bgc-summary-bignum">{{ currentRun.totalMatches }}</div>
          </div>
          <div class="bgc-summary-cell">
            <div class="bgc-summary-label">Best Score</div>
            <div class="bgc-summary-bignum">
              <ng-container *ngIf="currentRun.matches && currentRun.matches.length > 0; else noScore">
                {{ currentRun.matches[0].matchScore }}<span class="bgc-summary-unit">/100</span>
              </ng-container>
              <ng-template #noScore>—</ng-template>
            </div>
          </div>
        </div>

        <div class="bgc-summary-actions">
          <button mat-flat-button color="primary" (click)="activeSection='Intake'; runCheck()" [disabled]="isRunning">
            <mat-icon>autorenew</mat-icon> Re-run
          </button>
          <button mat-stroked-button class="bgc-btn-done" (click)="closeDialog()">
            <mat-icon>check</mat-icon> Done
          </button>
        </div>
      </div>

      <!-- Bottom half: full record for whichever card the user clicked. -->
      <div class="bgc-summary-detail">

        <div class="bgc-summary-detail-empty" *ngIf="!selectedMatch">
          <mat-icon>person_search</mat-icon>
          <div class="bgc-summary-detail-empty-title">No match selected</div>
          <div class="bgc-summary-detail-empty-hint">Click any card on the left to see the full record here.</div>
        </div>

        <ng-container *ngIf="selectedMatch">
          <div class="bgc-summary-detail-head">
            <div class="bgc-summary-detail-photo">
              <img *ngIf="selectedMatch.photoUrl" [src]="selectedMatch.photoUrl" alt="" />
              <div *ngIf="!selectedMatch.photoUrl" class="bgc-match-photo-fallback">{{ initials(selectedMatch.fullName) }}</div>
            </div>
            <div class="bgc-summary-detail-name">
              <h4>{{ selectedMatch.fullName }}</h4>
              <div class="bgc-summary-detail-sub">
                <span *ngIf="selectedMatch.sex">{{ selectedMatch.sex }}</span>
                <span *ngIf="selectedMatch.age">· Age {{ selectedMatch.age }}</span>
                <span *ngIf="selectedMatch.sourceState">· {{ selectedMatch.sourceState }}</span>
              </div>
            </div>
            <button mat-icon-button
                    class="bgc-summary-detail-close"
                    matTooltip="Close"
                    (click)="clearSelectedMatch()">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="bgc-summary-detail-lines">
            <div class="bgc-summary-detail-line" *ngIf="selectedMatch.middleName"><strong>Middle:</strong> {{ selectedMatch.middleName }}</div>
            <div class="bgc-summary-detail-line"><strong>Reg. ID:</strong> {{ selectedMatch.registrationId || '—' }}</div>
            <div class="bgc-summary-detail-line"><strong>Offender ID:</strong> {{ selectedMatch.offenderId || '—' }}</div>
            <div class="bgc-summary-detail-line"><strong>DOB:</strong> {{ formatDate(selectedMatch.dateOfBirth) }}</div>
            <div class="bgc-summary-detail-line"><strong>Address:</strong> {{ formatAddress(selectedMatch) || '—' }}</div>
            <div class="bgc-summary-detail-line" *ngIf="selectedMatch.latitude !== null && selectedMatch.latitude !== undefined">
              <strong>Coords:</strong> {{ selectedMatch.latitude | number:'1.4-6' }}, {{ selectedMatch.longitude | number:'1.4-6' }}
            </div>
            <div class="bgc-summary-detail-line"><strong>Type:</strong> {{ selectedMatch.registrationType || '—' }}</div>
            <div class="bgc-summary-detail-line"><strong>Tier:</strong> {{ selectedMatch.tierLevel || '—' }}</div>
            <div class="bgc-summary-detail-line"><strong>Risk:</strong> {{ selectedMatch.riskLevel || '—' }}</div>
            <div class="bgc-summary-detail-line"><strong>Status:</strong> {{ selectedMatch.registrationStatus || '—' }}</div>
            <div class="bgc-summary-detail-line" *ngIf="selectedMatch.convictionOffense || selectedMatch.convictionDate">
              <strong>Conviction:</strong> {{ selectedMatch.convictionOffense || '—' }}
              <span *ngIf="selectedMatch.convictionDate"> · {{ formatDate(selectedMatch.convictionDate) }}</span>
            </div>
            <div class="bgc-summary-detail-line" *ngIf="selectedMatch.registrationDate"><strong>Registered:</strong> {{ formatDate(selectedMatch.registrationDate) }}</div>
            <div class="bgc-summary-detail-line" *ngIf="selectedMatch.distanceMiles !== null && selectedMatch.distanceMiles !== undefined">
              <strong>Distance:</strong> {{ selectedMatch.distanceMiles | number:'1.1-1' }} mi
            </div>
            <div class="bgc-summary-detail-line"><strong>Reasoning:</strong> {{ selectedMatch.matchReasons || '—' }}</div>
          </div>
        </ng-container>

      </div>

    </aside>
  </div>
</div>
